import React from 'react'
import { 
  FolderIcon, 
  CheckCircleIcon, 
  ClockIcon, 
  UserGroupIcon 
} from '@heroicons/react/24/outline'
import { useAuth } from '@/hooks/useAuth'

const DashboardPage: React.FC = () => {
  const { user } = useAuth()

  const stats = [
    {
      name: 'Active Projects',
      value: '12',
      change: '+2.5%',
      icon: FolderIcon,
      color: 'text-primary-600',
      bgColor: 'bg-primary-100',
    },
    {
      name: 'Tasks Completed',
      value: '145',
      change: '+12%',
      icon: CheckCircleIcon,
      color: 'text-success-600',
      bgColor: 'bg-success-100',
    },
    {
      name: 'Hours Tracked',
      value: '340.5',
      change: '+8.2%',
      icon: ClockIcon,
      color: 'text-warning-600',
      bgColor: 'bg-warning-100',
    },
    {
      name: 'Team Members',
      value: '24',
      change: '+3',
      icon: UserGroupIcon,
      color: 'text-secondary-600',
      bgColor: 'bg-secondary-100',
    },
  ]

  const recentProjects = [
    { name: 'Website Redesign', client: 'Acme Corp', status: 'In Progress', progress: 75 },
    { name: 'Mobile App', client: 'TechStart', status: 'Planning', progress: 25 },
    { name: 'Brand Identity', client: 'Creative Co', status: 'Review', progress: 90 },
  ]

  const recentTasks = [
    { title: 'Update homepage design', project: 'Website Redesign', due: '2 days', priority: 'High' },
    { title: 'API integration', project: 'Mobile App', due: '1 week', priority: 'Medium' },
    { title: 'Logo finalization', project: 'Brand Identity', due: 'Today', priority: 'Urgent' },
  ]

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      {/* Welcome Header */}
      <div className="mb-8">
        <h1 className="text-2xl font-bold text-gray-900">
          Welcome back, {user?.user_metadata?.first_name}! 👋
        </h1>
        <p className="mt-1 text-sm text-gray-600">
          Here's what's happening with your projects today.
        </p>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
        {stats.map((stat) => (
          <div key={stat.name} className="card">
            <div className="card-body">
              <div className="flex items-center">
                <div className={`flex-shrink-0 ${stat.bgColor} p-3 rounded-lg`}>
                  <stat.icon className={`h-6 w-6 ${stat.color}`} />
                </div>
                <div className="ml-4 flex-1">
                  <p className="text-sm font-medium text-gray-600">{stat.name}</p>
                  <div className="flex items-baseline">
                    <p className="text-2xl font-semibold text-gray-900">{stat.value}</p>
                    <p className="ml-2 text-sm font-medium text-success-600">
                      {stat.change}
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
        {/* Recent Projects */}
        <div className="card">
          <div className="card-header">
            <h3 className="text-lg font-medium text-gray-900">Recent Projects</h3>
          </div>
          <div className="card-body">
            <div className="space-y-4">
              {recentProjects.map((project, index) => (
                <div key={index} className="flex items-center justify-between">
                  <div className="flex-1">
                    <p className="text-sm font-medium text-gray-900">{project.name}</p>
                    <p className="text-xs text-gray-500">{project.client}</p>
                  </div>
                  <div className="flex items-center space-x-4">
                    <div className="w-24">
                      <div className="bg-gray-200 rounded-full h-2">
                        <div 
                          className="bg-primary-600 h-2 rounded-full" 
                          style={{ width: `${project.progress}%` }}
                        />
                      </div>
                      <p className="text-xs text-gray-500 mt-1">{project.progress}%</p>
                    </div>
                    <span className={`badge ${
                      project.status === 'In Progress' ? 'badge-primary' :
                      project.status === 'Planning' ? 'badge-warning' :
                      'badge-success'
                    }`}>
                      {project.status}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          </div>
          <div className="card-footer">
            <button className="text-sm text-primary-600 hover:text-primary-500 font-medium">
              View all projects
            </button>
          </div>
        </div>

        {/* Recent Tasks */}
        <div className="card">
          <div className="card-header">
            <h3 className="text-lg font-medium text-gray-900">Upcoming Tasks</h3>
          </div>
          <div className="card-body">
            <div className="space-y-4">
              {recentTasks.map((task, index) => (
                <div key={index} className="flex items-center justify-between">
                  <div className="flex-1">
                    <p className="text-sm font-medium text-gray-900">{task.title}</p>
                    <p className="text-xs text-gray-500">{task.project}</p>
                  </div>
                  <div className="flex items-center space-x-4">
                    <p className="text-xs text-gray-500">Due {task.due}</p>
                    <span className={`badge ${
                      task.priority === 'Urgent' ? 'badge-error' :
                      task.priority === 'High' ? 'badge-warning' :
                      'badge-gray'
                    }`}>
                      {task.priority}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          </div>
          <div className="card-footer">
            <button className="text-sm text-primary-600 hover:text-primary-500 font-medium">
              View all tasks
            </button>
          </div>
        </div>
      </div>

      {/* Quick Actions */}
      <div className="mt-8 grid grid-cols-1 md:grid-cols-3 gap-6">
        <button className="card hover:shadow-medium transition-shadow">
          <div className="card-body text-center">
            <FolderIcon className="h-8 w-8 text-primary-600 mx-auto mb-2" />
            <h3 className="text-sm font-medium text-gray-900">New Project</h3>
            <p className="text-xs text-gray-500">Start a new project</p>
          </div>
        </button>

        <button className="card hover:shadow-medium transition-shadow">
          <div className="card-body text-center">
            <CheckCircleIcon className="h-8 w-8 text-success-600 mx-auto mb-2" />
            <h3 className="text-sm font-medium text-gray-900">Add Task</h3>
            <p className="text-xs text-gray-500">Create a new task</p>
          </div>
        </button>

        <button className="card hover:shadow-medium transition-shadow">
          <div className="card-body text-center">
            <ClockIcon className="h-8 w-8 text-warning-600 mx-auto mb-2" />
            <h3 className="text-sm font-medium text-gray-900">Start Timer</h3>
            <p className="text-xs text-gray-500">Track your time</p>
          </div>
        </button>
      </div>
    </div>
  )
}

export default DashboardPage